import type { AuthSession } from '@shared/types/auth';
import { logger } from '@shared/utils/logger';

/**
 * Cross-tab refresh coordination and cross-application session handoff.
 *
 * ## Why refreshes must be serialized
 *
 * identity-service rotates the refresh token on every `/auth/refresh` and
 * treats a rotated-out token presented again as REPLAY: it revokes the whole
 * session (`AuthService.refresh`, no grace window). Several tabs of the same
 * application restored together (browser restart) would each call refresh
 * with the same cookie at the same moment — the first rotates it, the others
 * present the old token, and the session is revoked.
 *
 * Within one origin we serialize refreshes per session with the Web Locks API
 * and share the result over a BroadcastChannel: a tab that waited on the lock
 * reuses a session another tab obtained a moment ago instead of refreshing
 * again. The access token is shared only between same-origin tabs of the same
 * application, which could already read each other's memory-equivalent state.
 *
 * ## Across applications
 *
 * HRMS, Finance and the Portal are separate origins, so they cannot share a
 * lock. Switching applications therefore navigates in the SAME tab (the source
 * application unloads before the target refreshes) and hands the session id
 * over in the URL fragment — see `buildHandoffUrl`. The session id is not a
 * secret (it is the access token's `sid` claim; the secret is the HttpOnly
 * cookie value), and a fragment is never sent to a server or written to
 * access logs. It is removed from the address bar on arrival.
 */

const CHANNEL_NAME = 'hexaunity-session';
const LOCK_PREFIX = 'hexaunity-refresh:';
/** A session another tab refreshed this recently is reused instead of refreshing again. */
const REUSE_WINDOW_MS = 10_000;

interface RefreshedMessage {
  type: 'refreshed';
  sessionId: string;
  session: AuthSession;
  at: number;
}

interface SignedOutMessage {
  type: 'signed-out';
  sessionId: string | null;
}

type SessionMessage = RefreshedMessage | SignedOutMessage;

const recent = new Map<string, { session: AuthSession; at: number }>();
let channel: BroadcastChannel | null = null;
const signedOutListeners = new Set<(sessionId: string | null) => void>();

function getChannel(): BroadcastChannel | null {
  if (channel || typeof BroadcastChannel === 'undefined') return channel;
  channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = (event: MessageEvent<SessionMessage>) => {
    const message = event.data;
    if (message?.type === 'refreshed') {
      recent.set(message.sessionId, { session: message.session, at: message.at });
    } else if (message?.type === 'signed-out') {
      signedOutListeners.forEach((listener) => listener(message.sessionId));
    }
  };
  return channel;
}

/**
 * Run `refresh` for `sessionId` at most once at a time across this origin's
 * tabs, reusing a result another tab produced within the reuse window.
 *
 * `rejectedToken` is the access token the server just refused. A recent
 * session carrying that same token is no answer — the session may have been
 * revoked since — so it is never reused; identity decides.
 */
export async function coordinateRefresh(
  sessionId: string | null,
  refresh: () => Promise<AuthSession>,
  rejectedToken?: string | null,
): Promise<AuthSession> {
  const key = sessionId ?? 'default';
  const run = async (): Promise<AuthSession> => {
    const shared = recent.get(key);
    const reusable =
      shared &&
      Date.now() - shared.at < REUSE_WINDOW_MS &&
      (!rejectedToken || shared.session.accessToken !== rejectedToken);
    if (shared && reusable) return shared.session;
    const session = await refresh();
    const at = Date.now();
    recent.set(session.sessionId ?? key, { session, at });
    try {
      getChannel()?.postMessage({
        type: 'refreshed',
        sessionId: session.sessionId ?? key,
        session,
        at,
      } satisfies RefreshedMessage);
    } catch (error) {
      logger.debug('Session broadcast failed', error);
    }
    return session;
  };

  getChannel();
  const locks = typeof navigator !== 'undefined' ? navigator.locks : undefined;
  if (!locks?.request) return run();
  return locks.request(`${LOCK_PREFIX}${key}`, run);
}

/** Tell the other tabs of this application that a session ended. */
export function broadcastSignedOut(sessionId: string | null): void {
  if (sessionId) recent.delete(sessionId);
  try {
    getChannel()?.postMessage({ type: 'signed-out', sessionId } satisfies SignedOutMessage);
  } catch (error) {
    logger.debug('Sign-out broadcast failed', error);
  }
}

/** Subscribe to sign-outs performed in another tab of this application. */
export function onSignedOutElsewhere(listener: (sessionId: string | null) => void): () => void {
  getChannel();
  signedOutListeners.add(listener);
  return () => signedOutListeners.delete(listener);
}

// ---------------------------------------------------------------------------
// Cross-application handoff
// ---------------------------------------------------------------------------

const HANDOFF_PARAM = 'hx_sid';

/**
 * URL of another application that continues the current session — its root,
 * or a path inside it (`/finance/asset-management/assets/<id>`).
 */
export function buildHandoffUrl(appBaseUrl: string, sessionId: string | null, path = '/'): string {
  const url = new URL(path, appBaseUrl);
  if (sessionId) url.hash = `${HANDOFF_PARAM}=${encodeURIComponent(sessionId)}`;
  return url.toString();
}

/**
 * Read and strip a handed-over session id from the address bar. Returns null
 * when the page was not opened through the application switcher.
 *
 * Destructive: a second call finds nothing. The session bootstrap reads it
 * through `takeHandoffSessionId`, which remembers the answer.
 */
export function consumeHandoffSessionId(): string | null {
  if (typeof window === 'undefined') return null;
  const hash = window.location.hash.replace(/^#/, '');
  if (!hash) return null;
  const params = new URLSearchParams(hash);
  const sessionId = params.get(HANDOFF_PARAM);
  if (!sessionId) return null;
  params.delete(HANDOFF_PARAM);
  const rest = params.toString();
  const clean = `${window.location.pathname}${window.location.search}${rest ? `#${rest}` : ''}`;
  window.history.replaceState(window.history.state, '', clean);
  // A session id is a UUID; refuse anything else rather than send it on.
  return /^[0-9a-f-]{36}$/i.test(sessionId) ? sessionId : null;
}

let handoff: { read: boolean; sessionId: string | null } = { read: false, sessionId: null };

/**
 * The session id handed over to THIS page load: read from the address bar the
 * first time, remembered afterwards.
 *
 * React runs effects twice in development (StrictMode), and reading the
 * fragment strips it. The second run of the session bootstrap therefore found
 * no handoff and refreshed this application's own last session — or no named
 * session at all — at the same moment as the handed-over one. Two refreshes
 * then presented the same refresh cookie, which identity-service treats as
 * replay and answers by revoking the session: the switch ended on the sign-in
 * page, or in a different saved account. Remembering the answer gives both
 * runs the same session id, so they share ONE refresh (see
 * `refreshAccessToken`).
 */
export function takeHandoffSessionId(): string | null {
  if (!handoff.read) handoff = { read: true, sessionId: consumeHandoffSessionId() };
  return handoff.sessionId;
}
